import { useState, useEffect, useRef } from "react";
import {
  ChevronDown,
  LogOut,
  User,
  Settings,
  Archive,
  Headset,
  Home,
  BarChart3,
  Server,
  Layers,
  Menu,
  X,
  Brain,
  Thermometer,
  Headphones,
  DollarSign,
  Activity,
  Wrench,
  TrendingUp,
  ShieldCheck,
  Send,
  Armchair,
} from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import { hasPermission } from "../../utils/permissions";
import NotificationBell from "./NotificationBell";

const Navbar = () => {
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const [openMobileGroup, setOpenMobileGroup] = useState(null);

  const navigate = useNavigate();
  const location = useLocation();
  const userMenuRef = useRef(null);
  const navRef = useRef(null);

  const user = JSON.parse(localStorage.getItem("user") || "{}");
  const isSuperAdmin = user?.email === "jarenas@previonix.com";
  const planActual = (localStorage.getItem("planActual") || "FREE").toUpperCase();
  const canAccessPredictivo = ["PRO", "BLACK"].includes(planActual);
  const canAccessIoT = ["BLACK"].includes(planActual);

  const isActive = (path) => location.pathname === path;
  const isGroupActive = (paths) => paths.some((p) => location.pathname === p);

  // Cierra dropdowns al hacer clic fuera
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) {
        setUserMenuOpen(false);
      }
      if (navRef.current && !navRef.current.contains(e.target)) {
        setOpenDropdown(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Cierra menú móvil al navegar
  useEffect(() => {
    setMobileOpen(false);
    setOpenDropdown(null);
    setOpenMobileGroup(null);
  }, [location.pathname]);

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    localStorage.removeItem("servicioActivo");
    sessionStorage.clear();
    navigate("/login");
    window.history.pushState(null, "", "/login");
  };

  const go = (route) => {
    navigate(route);
    setMobileOpen(false);
  };

  // Estilos de item suelto (desktop)
  const navItem = (path) =>
    `flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-150 ${
      isActive(path)
        ? "bg-blue-600 text-white shadow-sm"
        : "text-gray-600 hover:text-blue-600 hover:bg-blue-50"
    }`;

  // Estilo botón de grupo (desktop)
  const groupBtn = (paths) =>
    `flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-150 ${
      isGroupActive(paths)
        ? "bg-blue-600 text-white shadow-sm"
        : "text-gray-600 hover:text-blue-600 hover:bg-blue-50"
    }`;

  // Grupos de navegación
  const groups = [
    {
      id: "operaciones",
      label: "Operaciones",
      icon: <Wrench className="h-4 w-4" />,
      paths: ["/equipos", "/mobiliario", "/my_services", "/services", "/storage"],
      items: [
        {
          label: "Equipos",
          path: "/equipos",
          icon: <Server className="h-4 w-4" />,
          desc: "Inventario de equipos biomédicos",
          show: hasPermission("equipos.ver"),
        },
        {
          label: "Mobiliario",
          path: "/mobiliario",
          icon: <Armchair className="h-4 w-4" />,
          desc: "Sillas, mesas, escritorios y otros elementos",
          show: hasPermission("mobiliario.ver"),
        },
        {
          label: "Mis servicios",
          path: "/my_services",
          icon: <Headset className="h-4 w-4" />,
          desc: "Servicios asignados a ti",
          show: hasPermission("servicios.gestionar"),
        },
        {
          label: "Servicios",
          path: "/services",
          icon: <Headset className="h-4 w-4" />,
          desc: "Gestión general de servicios",
          show: hasPermission("servicios.ver"),
        },
        {
          label: "Almacén",
          path: "/storage",
          icon: <Archive className="h-4 w-4" />,
          desc: "Stock e inventario de repuestos",
          show: hasPermission("almacen.ver"),
        },
      ],
    },
    {
      id: "analisis",
      label: "Análisis",
      icon: <TrendingUp className="h-4 w-4" />,
      paths: ["/reports", "/predictivo", "/environmental"],
      items: [
        {
          label: "Reportes",
          path: "/reports",
          icon: <BarChart3 className="h-4 w-4" />,
          desc: "Informes y estadísticas",
          show: hasPermission("reportes.ver"),
        },
        {
          label: "Predictivo IA",
          path: "/predictivo",
          icon: <Brain className="h-4 w-4" />,
          desc: "Mantenimiento predictivo con IA",
          show: hasPermission("equipos.ver") && canAccessPredictivo,
          badge: "PRO",
        },
        {
          label: "Sensores IoT",
          path: "/environmental",
          icon: <Thermometer className="h-4 w-4" />,
          desc: "Monitoreo ambiental en tiempo real",
          show: hasPermission("equipos.ver") && canAccessIoT,
          badge: "BLACK",
        },
      ],
    },
    {
      id: "finanzas",
      label: "Finanzas",
      icon: <DollarSign className="h-4 w-4" />,
      paths: ["/costos-cliente", "/tco"],
      items: [
        {
          label: "Costos",
          path: "/costos-cliente",
          icon: <DollarSign className="h-4 w-4" />,
          desc: "Control de costos por cliente",
          show: hasPermission("costos_cliente.ver"),
        },
        {
          label: "TCO",
          path: "/tco",
          icon: <Activity className="h-4 w-4" />,
          desc: "Costo total de propiedad",
          show: hasPermission("tco.ver"),
        },
      ],
    },
    {
      id: "admin",
      label: "Administración",
      icon: <ShieldCheck className="h-4 w-4" />,
      paths: ["/users", "/config"],
      items: [
        {
          label: "Usuarios",
          path: "/users",
          icon: <Layers className="h-4 w-4" />,
          desc: "Gestión de roles y accesos",
          show: hasPermission("usuarios.ver"),
        },
        {
          label: "Configuración",
          path: "/config",
          icon: <Settings className="h-4 w-4" />,
          desc: "Ajustes de la plataforma",
          show: hasPermission("config.ver"),
        },
      ],
    },
  ];

  // Filtra grupos que tengan al menos un ítem visible
  const visibleGroups = groups.filter((g) => g.items.some((i) => i.show));

  return (
    <header className="w-full bg-white shadow-sm border-b border-gray-200 fixed top-0 z-50">
      {/* ── Barra superior ── */}
      <div className="flex items-center justify-between px-4 md:px-6 h-16">
        {/* Logo */}
        <div
          className="flex items-center cursor-pointer shrink-0"
          onClick={() => navigate("/dashboard")}
        >
          <img
            src="/logo.png"
            alt="Previonix"
            className="h-10 w-auto object-contain"
          />
        </div>

        {/* Zona derecha: campana (siempre visible) + toggle móvil + zona desktop */}
        <div className="flex items-center gap-2">
          <NotificationBell />

          {/* Toggle móvil */}
          <button
            className="md:hidden p-2 rounded-lg text-gray-500 hover:bg-gray-100 transition-colors"
            onClick={() => setMobileOpen((v) => !v)}
            aria-label="Abrir menú"
          >
            {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>

          {/* Zona derecha desktop */}
          <div className="hidden md:flex items-center gap-4">
            {/* Indicador servidor */}
            <div className="flex items-center gap-2 text-xs text-gray-500 bg-gray-50 border border-gray-200 rounded-full px-3 py-1.5">
              <span className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse" />
              Servidor en línea
            </div>

            {/* Menú usuario */}
            <div className="relative" ref={userMenuRef}>
              <button
                data-tour="user-menu"
                onClick={() => setUserMenuOpen((v) => !v)}
                className="flex items-center gap-2.5 pl-1 pr-3 py-1 rounded-full hover:bg-gray-100 transition-colors border border-transparent hover:border-gray-200"
              >
                <img
                src={
                  user?.foto && user.foto.startsWith("data:image")
                    ? user.foto
                    : "/user-avatar.png"
                }
                alt="Avatar"
                className="h-8 w-8 rounded-full object-cover border border-gray-200"
              />
              <span className="text-sm font-medium text-gray-700 truncate max-w-[120px]">
                {user?.name || "Administrador"}
              </span>
              <ChevronDown
                className={`h-3.5 w-3.5 text-gray-400 transition-transform duration-200 ${
                  userMenuOpen ? "rotate-180" : ""
                }`}
              />
            </button>

            {userMenuOpen && (
              <div className="absolute right-0 mt-2 w-48 bg-white border border-gray-200 rounded-xl shadow-lg overflow-hidden">
                <div className="px-4 py-3 border-b border-gray-100 bg-gray-50">
                  <p className="text-xs text-gray-500">Conectado como</p>
                  <p className="text-sm font-semibold text-gray-800 truncate">
                    {user?.name || "Administrador"}
                  </p>
                </div>
                <button
                  className="flex items-center w-full px-4 py-2.5 text-sm text-gray-700 hover:bg-blue-50 hover:text-blue-600 transition-colors"
                  onClick={() => { navigate("/profile"); setUserMenuOpen(false); }}
                >
                  <User className="h-4 w-4 mr-2.5" /> Mi perfil
                </button>
                <hr className="border-gray-100" />
                <button
                  onClick={handleLogout}
                  className="flex items-center w-full px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 transition-colors"
                >
                  <LogOut className="h-4 w-4 mr-2.5" /> Cerrar sesión
                </button>
              </div>
            )}
          </div>
          </div>
        </div>
      </div>

      {/* ── Nav desktop / tablet ── */}
      <nav
        ref={navRef}
        className="hidden md:flex bg-white border-t border-gray-100 px-4 md:px-6 h-11 items-center"
      >
        <div className="flex items-center justify-center gap-1 w-full">
          {isSuperAdmin ? (
            <>
              <button
                onClick={() => navigate("/adminDashboard")}
                className={navItem("/adminDashboard")}
              >
                <BarChart3 className="h-4 w-4" />
                Dashboard Global
              </button>
              <button
                onClick={() => navigate("/adminDashboard/notificaciones")}
                className={navItem("/adminDashboard/notificaciones")}
              >
                <Send className="h-4 w-4" />
                Notificaciones
              </button>
            </>
          ) : (
            <>
              {/* Dashboard */}
              {hasPermission("dashboard.ver") && (
                <button
                  data-tour="nav-dashboard"
                  onClick={() => navigate("/dashboard")}
                  className={navItem("/dashboard")}
                >
                  <Home className="h-4 w-4" />
                  Dashboard
                </button>
              )}

              {/* Grupos con dropdown */}
              {visibleGroups.map((group) => (
                <div key={group.id} className="relative">
                  <button
                    data-tour={
                      group.id === "operaciones"
                        ? "nav-operaciones"
                        : group.id === "admin"
                        ? "nav-admin"
                        : undefined
                    }
                    onClick={() =>
                      setOpenDropdown((v) => (v === group.id ? null : group.id))
                    }
                    className={`${groupBtn(group.paths)} gap-1`}
                  >
                    {group.icon}
                    {group.label}
                    <ChevronDown
                      className={`h-3.5 w-3.5 ml-0.5 transition-transform duration-200 ${
                        openDropdown === group.id ? "rotate-180" : ""
                      }`}
                    />
                  </button>

                  {openDropdown === group.id && (
                    <div className="absolute top-full left-0 mt-2 w-64 bg-white border border-gray-200 rounded-xl shadow-xl overflow-hidden z-50">
                      <div className="px-3 py-2 bg-gray-50 border-b border-gray-100">
                        <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">
                          {group.label}
                        </p>
                      </div>
                      {group.items
                        .filter((i) => i.show)
                        .map((item) => (
                          <button
                            key={item.path}
                            onClick={() => {
                              navigate(item.path);
                              setOpenDropdown(null);
                            }}
                            className={`flex items-start w-full px-4 py-3 text-left transition-colors ${
                              isActive(item.path)
                                ? "bg-blue-50 text-blue-700"
                                : "text-gray-700 hover:bg-gray-50"
                            }`}
                          >
                            <span
                              className={`mt-0.5 mr-3 shrink-0 ${
                                isActive(item.path)
                                  ? "text-blue-600"
                                  : "text-gray-400"
                              }`}
                            >
                              {item.icon}
                            </span>
                            <span className="flex-1 min-w-0">
                              <span className="flex items-center gap-2">
                                <span className="text-sm font-medium leading-tight">
                                  {item.label}
                                </span>
                                {item.badge && (
                                  <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-blue-100 text-blue-700 leading-none">
                                    {item.badge}
                                  </span>
                                )}
                              </span>
                              <span className="text-xs text-gray-400 mt-0.5 block">
                                {item.desc}
                              </span>
                            </span>
                            {isActive(item.path) && (
                              <span className="w-1.5 h-1.5 rounded-full bg-blue-600 mt-1.5 shrink-0" />
                            )}
                          </button>
                        ))}
                    </div>
                  )}
                </div>
              ))}

              {/* Soporte suelto */}
              {hasPermission("soporte.ver") && (
                <button
                  onClick={() => navigate("/soporte")}
                  className={navItem("/soporte")}
                >
                  <Headphones className="h-4 w-4" />
                  Soporte
                </button>
              )}
            </>
          )}
        </div>
      </nav>

      {/* ── Menú móvil ── */}
      {mobileOpen && (
        <div className="md:hidden bg-white border-t border-gray-200 overflow-y-auto max-h-[calc(100vh-4rem)]">
          <div className="px-4 py-3 space-y-1">
            {isSuperAdmin ? (
              <>
                <button onClick={() => go("/adminDashboard")} className={navItem("/adminDashboard")}>
                  <BarChart3 className="h-4 w-4" /> Dashboard Global
                </button>
                <button onClick={() => go("/adminDashboard/notificaciones")} className={navItem("/adminDashboard/notificaciones")}>
                  <Send className="h-4 w-4" /> Notificaciones
                </button>
              </>
            ) : (
              <>
                {hasPermission("dashboard.ver") && (
                  <button onClick={() => go("/dashboard")} className={navItem("/dashboard")}>
                    <Home className="h-4 w-4" /> Dashboard
                  </button>
                )}

                {visibleGroups.map((group) => {
                  const visibleItems = group.items.filter((i) => i.show);
                  const groupIsActive = isGroupActive(group.paths);
                  const isExpanded = openMobileGroup === group.id;

                  return (
                    <div key={group.id} className="rounded-lg overflow-hidden border border-gray-100">
                      <button
                        onClick={() =>
                          setOpenMobileGroup((v) =>
                            v === group.id ? null : group.id
                          )
                        }
                        className={`flex items-center w-full px-3 py-2.5 text-sm font-medium transition-colors ${
                          groupIsActive
                            ? "bg-blue-50 text-blue-700"
                            : "text-gray-700 hover:bg-gray-50"
                        }`}
                      >
                        <span className={`mr-2 ${groupIsActive ? "text-blue-600" : "text-gray-400"}`}>
                          {group.icon}
                        </span>
                        <span className="flex-1 text-left">{group.label}</span>
                        <ChevronDown
                          className={`h-4 w-4 text-gray-400 transition-transform duration-200 ${
                            isExpanded ? "rotate-180" : ""
                          }`}
                        />
                      </button>

                      {isExpanded && (
                        <div className="bg-gray-50 border-t border-gray-100 divide-y divide-gray-100">
                          {visibleItems.map((item) => (
                            <button
                              key={item.path}
                              onClick={() => go(item.path)}
                              className={`flex items-center w-full px-4 py-2.5 text-sm transition-colors ${
                                isActive(item.path)
                                  ? "text-blue-700 font-medium bg-blue-50"
                                  : "text-gray-600 hover:text-blue-600 hover:bg-white"
                              }`}
                            >
                              <span className={`mr-3 ${isActive(item.path) ? "text-blue-500" : "text-gray-400"}`}>
                                {item.icon}
                              </span>
                              <span className="flex-1 text-left">{item.label}</span>
                              {item.badge && (
                                <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-blue-100 text-blue-700">
                                  {item.badge}
                                </span>
                              )}
                              {isActive(item.path) && (
                                <span className="w-1.5 h-1.5 rounded-full bg-blue-600 ml-2" />
                              )}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}

                {hasPermission("soporte.ver") && (
                  <button onClick={() => go("/soporte")} className={navItem("/soporte")}>
                    <Headphones className="h-4 w-4" /> Soporte
                  </button>
                )}
              </>
            )}

            {/* Separador */}
            <div className="border-t border-gray-200 pt-2 mt-2 space-y-1">
              <button
                onClick={() => go("/profile")}
                className="flex items-center w-full px-3 py-2 text-sm text-gray-700 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <User className="h-4 w-4 mr-2.5 text-gray-400" /> Mi perfil
              </button>
              <button
                onClick={handleLogout}
                className="flex items-center w-full px-3 py-2 text-sm text-red-600 rounded-lg hover:bg-red-50 transition-colors"
              >
                <LogOut className="h-4 w-4 mr-2.5" /> Cerrar sesión
              </button>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default Navbar;
